import type {
  SalesOrderSettings,
  UpdateSalesOrderSettingsRequest,
} from '@/types/salesOrderSettings'

/**
 * The settings each section of the page owns, lifted out of the whole object and put back into it.
 *
 * The pages keep one draft of the entire settings object and post it back whole, so a section
 * reads and writes through a pair of functions here rather than touching field names itself.
 */

export interface AutomationSettings {
  fullyAutomaticValidation: boolean
}

export function automationFromSettings(settings: SalesOrderSettings): AutomationSettings {
  return { fullyAutomaticValidation: settings.fully_automatic_validation }
}

export function applyAutomationToSettings(
  base: SalesOrderSettings,
  automation: AutomationSettings,
): SalesOrderSettings {
  return { ...base, fully_automatic_validation: automation.fullyAutomaticValidation }
}

/** The two amounts above which an order is put in front of a person rather than created outright. */
export interface PriceThresholds {
  totalOrderPrice: number
  totalRowPrice: number
}

export function thresholdsFromSettings(settings: SalesOrderSettings): PriceThresholds {
  return {
    totalOrderPrice: settings.flag_total_order_price_threshold,
    totalRowPrice: settings.flag_total_row_price_threshold,
  }
}

export function applyThresholdsToSettings(
  base: SalesOrderSettings,
  thresholds: PriceThresholds,
): SalesOrderSettings {
  return {
    ...base,
    flag_total_order_price_threshold: thresholds.totalOrderPrice,
    flag_total_row_price_threshold: thresholds.totalRowPrice,
  }
}

/** The fields a mismatch in which should stop an order rather than let it through. */
export const SO_FLAG_FIELDS = [
  { id: 'flag_delivery_address', label: 'Delivery address' },
  { id: 'flag_contact_person', label: 'Contact person' },
  { id: 'flag_article_number', label: 'Article number' },
  { id: 'flag_article_price', label: 'Article price' },
] as const

export type SoFlagFieldId = (typeof SO_FLAG_FIELDS)[number]['id']

export function flaggedFieldsFromSettings(settings: SalesOrderSettings): Set<SoFlagFieldId> {
  return new Set(SO_FLAG_FIELDS.filter((field) => settings[field.id]).map((field) => field.id))
}

export function applyFlaggedFieldsToSettings(
  base: SalesOrderSettings,
  flagged: Set<SoFlagFieldId>,
): SalesOrderSettings {
  const next = { ...base }
  for (const field of SO_FLAG_FIELDS) {
    next[field.id] = flagged.has(field.id)
  }
  return next
}

export interface StockSettings {
  checkStockBalance: boolean
}

export function stockFromSettings(settings: SalesOrderSettings): StockSettings {
  return { checkStockBalance: settings.check_stock_balance }
}

export function applyStockToSettings(
  base: SalesOrderSettings,
  stock: StockSettings,
): SalesOrderSettings {
  return { ...base, check_stock_balance: stock.checkStockBalance }
}

export interface ErpSettings {
  uploadOrderDocumentToErp: boolean
}

export function erpFromSettings(settings: SalesOrderSettings): ErpSettings {
  return { uploadOrderDocumentToErp: settings.upload_order_document_to_erp }
}

export function applyErpToSettings(base: SalesOrderSettings, erp: ErpSettings): SalesOrderSettings {
  return { ...base, upload_order_document_to_erp: erp.uploadOrderDocumentToErp }
}

/** Where the sales order's delivery address and contact person are taken from. */
export interface OrderSourceSettings {
  useExtractedDeliveryAddress: boolean
  useExtractedCustomerContactPerson: boolean
}

export function orderSourceFromSettings(settings: SalesOrderSettings): OrderSourceSettings {
  return {
    useExtractedDeliveryAddress: settings.use_extracted_delivery_address,
    useExtractedCustomerContactPerson: settings.use_extracted_customer_contact_person,
  }
}

export function applyOrderSourceToSettings(
  base: SalesOrderSettings,
  source: OrderSourceSettings,
): SalesOrderSettings {
  return {
    ...base,
    use_extracted_delivery_address: source.useExtractedDeliveryAddress,
    use_extracted_customer_contact_person: source.useExtractedCustomerContactPerson,
  }
}

export interface ParseFormatSettings {
  useFloatParsingSettings: boolean
  floatParsingEnglishFormat: boolean
  dateParsingWeekFormat: boolean
  dateParsingAmericanFormat: boolean
  dateParsingIsoFormat: boolean
  dateParsingDayFirstFormat: boolean
}

export function parseFormatFromSettings(settings: SalesOrderSettings): ParseFormatSettings {
  return {
    useFloatParsingSettings: settings.use_float_parsing_settings,
    floatParsingEnglishFormat: settings.float_parsing_english_format,
    dateParsingWeekFormat: settings.date_parsing_week_format,
    dateParsingAmericanFormat: settings.date_parsing_american_format,
    dateParsingIsoFormat: settings.date_parsing_iso_format,
    dateParsingDayFirstFormat: settings.date_parsing_day_first_format,
  }
}

export function applyParseFormatToSettings(
  base: SalesOrderSettings,
  parseFormat: ParseFormatSettings,
): SalesOrderSettings {
  return {
    ...base,
    use_float_parsing_settings: parseFormat.useFloatParsingSettings,
    float_parsing_english_format: parseFormat.floatParsingEnglishFormat,
    date_parsing_week_format: parseFormat.dateParsingWeekFormat,
    date_parsing_american_format: parseFormat.dateParsingAmericanFormat,
    date_parsing_iso_format: parseFormat.dateParsingIsoFormat,
    date_parsing_day_first_format: parseFormat.dateParsingDayFirstFormat,
  }
}

export interface QuantitySettings {
  multiplyQuantityByUnitsPerPackage: boolean
}

export function quantityFromSettings(settings: SalesOrderSettings): QuantitySettings {
  return { multiplyQuantityByUnitsPerPackage: settings.multiply_quantity_by_units_per_package }
}

export function applyQuantityToSettings(
  base: SalesOrderSettings,
  quantity: QuantitySettings,
): SalesOrderSettings {
  return {
    ...base,
    multiply_quantity_by_units_per_package: quantity.multiplyQuantityByUnitsPerPackage,
  }
}

export function toUpdateRequest(settings: SalesOrderSettings): UpdateSalesOrderSettingsRequest {
  const { id: _id, ...body } = settings
  return body
}

export function settingsEqual(a: SalesOrderSettings, b: SalesOrderSettings): boolean {
  return JSON.stringify(a) === JSON.stringify(b)
}

/** At least one date format has to stay on, which the backend rejects a save without. */
export function hasADateFormat(settings: SalesOrderSettings): boolean {
  return (
    settings.date_parsing_week_format ||
    settings.date_parsing_american_format ||
    settings.date_parsing_iso_format ||
    settings.date_parsing_day_first_format
  )
}
